import React, { useEffect, useLayoutEffect, useState } from 'react';
import styled from 'styled-components';
import { Checkbox } from '/imports/ui/Checkbox';
import { strings } from '/imports/strings';
import { css } from '/imports/ui/css';
import { Input } from '/imports/ui/Input';
import { LoadingFill } from '/imports/ui/LoadingFill';
import { randomString, uniqueArray } from '/imports/utils';

const RichMultiSelectStyled = styled.div`
  position: relative;
  width: 100%;
  label {
    display: block;
    position: relative;
    padding: 0.25rem 0;
    .icon {
      left: 0;
      top: 0.9rem;
      position: absolute !important;
    }
  }
  input {
    width: 100%;
    &[type=checkbox] {
      /* appearance:none (from Checkbox) + display:inline makes this a non-replaced inline box in some
         browsers, which ignores width/height — it collapses horizontally and stretches to the line-height
         vertically (renders fine in others). inline-block with explicit dimensions forces a fixed box everywhere. */
      display: inline-block;
      box-sizing: border-box;
      width: 1.4rem;
      height: 1.5rem;
      min-width: 1.4rem;
      flex: none;
      margin-right: 0.5rem;
      position: relative;
      top: 0.2rem;
    }
    &::placeholder {
      color: ${css.grey1};
    }
  }
  .options {
    position: absolute;
    display: none;
    color: ${css.grey2};
    background: ${css.inputBackgroundColor};
    border: ${css.inputBorder};
    border-radius: ${css.inputBorderRadius};
    width: 100%;
    z-index: 1;
    &.open {
      display: block;
    }
  }
  .option, .select-all {
    padding: 0.5rem;
    cursor: pointer;
    display: block;
    color: ${css.grey1} !important;
    margin-bottom: 0 !important;
    span {
      position: relative;
      bottom: 0.2rem;
    }
    .checkmark {
      svg {
        position: relative;
        top: 0.15rem;
        left: 0.05rem;
      }
    }
  }
  .select-all {
    border-bottom: ${css.inputBorder};
    input {
      &[type=checkbox] {
        top: 0.4rem;
      }
    }
  }
  .no-results {
    text-transform: capitalize;
    min-height: 3rem;
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    text-align: center;
  }
  .loading {
    height: 4rem;
  }
  .scrollable-options {
    max-height: 29rem;
    overflow: auto;
    label {
      padding: 0.5rem;
    }
    .checkmark {
      position: relative;
      svg {
        position: relative;
        top: 0.8rem;
        left: 0.55rem;
      }
    }
  }
`;

interface RichMultiSelectProps {
  className?: string;
  placeholder?: string;
  options: any[];
  loading?: boolean;
  selectedValues?: string[];
  disabled?: boolean;
  fixedOptions?: boolean;  // fix-position the open options panel so overflow-clipping ancestors (modals) can't cut it off
  onSearch?(search: string | undefined, e: React.ChangeEvent<HTMLInputElement> | undefined): void;
  onChange?(selectedValues: string[]): void;
  onSelect?(selectedValues: string[], option: any, checked: boolean): void;
  onSelectAll?(checked: boolean): void;
  noResultsText?: string;
}

/** Multi-select dropdown with search, select-all, and checkbox options. */
export const RichMultiSelect = (props: RichMultiSelectProps) => {
  const { options, loading, disabled } = props;
  const [focusedOption,] = useState<any>();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState<string>();
  const [selectAll, setSelectAll] = useState(false);
  const [renderKey, setRenderKey] = useState(randomString());
  const [elementId] = useState(`rms${randomString()}`);
  const [selectedValues, setSelectedValues] = useState(props.selectedValues || []);
  useEffect(() => {
    if (props.selectedValues === undefined) return;
    setSelectedValues(props.selectedValues);
  }, [JSON.stringify(props.selectedValues)]);
  /**
   * Handles keyboard navigation within the options list.
   * @param e - keyboard event from the search input
   */
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      if (focusedOption) {
        // ToDo;
      }
    }
  };
  useEffect(() => {
    const closer = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      !target.closest(`#${elementId}`) && setOpen(false);
    };
    window.addEventListener('click', closer);
    return () => { window.removeEventListener('click', closer); };
  }, []);
  useEffect(() => { props.onChange?.(selectedValues); }, [JSON.stringify(selectedValues)]);
  /** Sizes the open options panel to the viewport and (with fixedOptions) pins it at its measured spot. */
  const setOptionsHeight = () => {
    const optionsEl = document.getElementById(`${elementId}-options`);
    if (!optionsEl) return;
    const rect = optionsEl.getBoundingClientRect();
    if (open && props.fixedOptions) {
      optionsEl.style.position = 'fixed';
      optionsEl.style.top = `${rect.top}px`;
      optionsEl.style.left = `${rect.left}px`;
      optionsEl.style.width = `${rect.width}px`;
    }
    else if (props.fixedOptions) {
      optionsEl.style.removeProperty('position');
      optionsEl.style.removeProperty('top');
      optionsEl.style.removeProperty('left');
      optionsEl.style.removeProperty('width');
    }
    // Size the SCROLL area (not the outer box) to the space beneath it, so a long list scrolls instead of
    // spilling past the viewport/modal. Measured AFTER any fixed-positioning above so its top is final.
    const scrollEl = optionsEl.querySelector('.scrollable-options') as HTMLElement | null;
    if (scrollEl) {
      scrollEl.style.maxHeight = `${Math.max(96, window.innerHeight - scrollEl.getBoundingClientRect().top - 16)}px`;
    }
  };
  // renderKey is in the deps because toggling an option bumps it, which remounts the panel's DOM (key=
  // renderKey) and drops the imperative position:fixed pin from setOptionsHeight — so it must be re-pinned,
  // or a fixedOptions panel reverts to position:absolute and gets clipped by a modal's overflow again.
  // Layout effect so the re-pin happens before paint (no one-frame flash of the un-pinned panel per toggle).
  useLayoutEffect(() => {
    setOptionsHeight();
    const handler = () => setOptionsHeight();
    window.addEventListener('resize', handler);
    return () => { window.removeEventListener('resize', handler); };
  }, [open, renderKey]);
  /**
   * Filters options by the current search term.
   * @param option - option to test
   * @returns true if the option matches the search term
   */
  const searchFilter = (option: any) => {
    const _search = search?.trim().toLowerCase();
    if (!_search) return true;
    return option.label?.toLowerCase().includes(_search);
  };
  const selectedOptions = options.filter((o) => selectedValues.includes(o.value));
  const placeholder = selectedOptions.length ?
    selectedOptions.map((o) => o.label).join(' • ') : props.placeholder || strings.Search();
  return (
    <RichMultiSelectStyled id={elementId} className={props.className} key={renderKey}>
      <Input
        placeholder={placeholder}
        disabled={disabled}
        value={search}
        onChange={(search, e) => {setSearch(search); props.onSearch?.(search, e);}}
        onFocus={() => setOpen(true)}
        onKeyPress={(e) => handleKeyPress(e)}
      />
      <div className={`options${open ? ' open' : ''}`} id={`${elementId}-options`}>
        <span className="select-all">
          <Checkbox
            checked={selectAll}
            onChange={(checked) => {
              const selectedValues = checked ? uniqueArray(options.map((o) => o.value)) : [];
              props.onSelectAll?.(checked);
              setSelectAll(checked);
              setSelectedValues(selectedValues);
              setRenderKey(randomString());
            }}
          >
            <span>{strings.SelectAll()}</span>
          </Checkbox>
        </span>
        <div className="scrollable-options">
          {
            options?.filter(searchFilter).map((option) => {
              return (
                <Checkbox
                  key={option.value}
                  className="option"
                  checked={selectedValues.includes(option.value)}
                  onChange={(checked) => {
                    const _selectedValues = checked ? uniqueArray([...selectedValues, option.value]) : selectedValues.filter((v) => v !== option.value);
                    props.onSelect?.(_selectedValues, option, checked);
                    setSelectedValues(_selectedValues);
                    setRenderKey(randomString());
                  }}
                >
                  <span>{option.label}</span>
                </Checkbox>
              );
            })
          }
          {
            options?.length === 0 ?
              (
                <div className="no-results">{props.noResultsText || strings.NoResults()}</div>
              )
              :
              null
          }
          {
            loading ?
              (
                <div className="loading">
                  <LoadingFill />
                </div>
              )
              :
              null
          }
        </div>
      </div>
    </RichMultiSelectStyled>
  );
};